import { obtenerMensajeError } from "utils/apiErrors";
import React, { useState, useEffect } from "react";
import PropTypes from "prop-types";
import { Typography, Divider } from "@mui/material";
import Grid from "@mui/material/Grid2";
import SuiBox from "components/SuiBox";

// API
import ProcesosApi from "api/procesosApi";

// Componentes
import FirmaCliente from "./components/firmaCliente";
import DocumentoIdentidad from "./components/documentoIdentidad";
import PersonasACargo from "./components/personasACargo";
import SociedadesProceso from "./components/sociedadesProceso";
import DocumentosBienes from "./components/documentosBienes";
import DocumentosLaborales from "./components/documentosLaborales";
import DocumentosAnexos from "./components/documentosAnexos";

const DocumentosForm = ({ procesoId, setError }) => {
  const [procesoIdentificador, setProcesoIdentificador] = useState(null);
  const [procesoData, setProcesoData] = useState(null);
  const [clienteData, setClienteData] = useState(null);

  useEffect(() => {
    const fetchProcesoData = async () => {
      try {
        if (procesoId) {
          const procesoResponse = await ProcesosApi.fetchProcesoById(procesoId);
          const clienteResponse = await ProcesosApi.fetchClientes();
          if (procesoResponse.data) {
            setProcesoData(procesoResponse.data);
            setProcesoIdentificador(procesoResponse.data.identificador);
            // Obtener la identificación del cliente desde el proceso
            const clienteIdentificacion = procesoResponse.data.cliente_detalle?.identificacion;
            // Buscar en la lista de clientes
            const cliente = clienteResponse.data.find((c) => {
              return c.identificacion === clienteIdentificacion;
            });
            if (cliente) {
              setClienteData(cliente);
            } else {
              // Si no se encuentra en la lista, usar los datos del proceso
              setClienteData(procesoResponse.data.cliente_detalle);
            }
          }
        }
      } catch (error) {
        console.error("Index - Error obteniendo datos del Proceso:", error);
        setError(obtenerMensajeError(error, "Error al cargar los datos del Proceso"));
      }
    };

    fetchProcesoData();
  }, [procesoId, setError]);

  // Verificar si debe mostrar documentos laborales
  const mostrarDocumentosLaborales =
    clienteData?.tipo_trabajador === "EMPLEADO(A)" ||
    clienteData?.tipo_trabajador === "INDEPENDIENTE";

  return (
    <SuiBox>
      {/* Sección de Documentos de la Solicitud */}
      <SuiBox>
        <Typography variant="h5" gutterBottom>
          Documentos Anexos de la Solicitud
        </Typography>
        <Grid container spacing={3}>
          <Grid size={6}>
            <DocumentoIdentidad
              procesoId={procesoId}
              identificador={procesoIdentificador}
              setError={setError}
            />
          </Grid>
          <Grid size={6}>
            <FirmaCliente
              procesoId={procesoId}
              identificador={procesoIdentificador}
              setError={setError}
            />
          </Grid>
        </Grid>
        <Divider sx={{ my: 3 }} />
      </SuiBox>

      {/* Sección de Documentos de Bienes (solo si posee bienes) */}
      {procesoData?.personas_a_cargo && (
        <SuiBox>
          <Typography variant="h6" gutterBottom>
            Documentos de Personas a Cargo
          </Typography>
          <PersonasACargo
            procesoId={procesoId}
            identificador={procesoIdentificador}
            setError={setError}
          />
          <Divider sx={{ my: 3 }} />
        </SuiBox>
      )}

      {/* Sección de Documentos de Bienes (solo si posee bienes) */}
      {procesoData?.sociedades && (
        <SuiBox>
          <Typography variant="h6" gutterBottom>
            Documentos de Sociedades
          </Typography>
          <SociedadesProceso
            procesoId={procesoId}
            identificador={procesoIdentificador}
            setError={setError}
          />
          <Divider sx={{ my: 3 }} />
        </SuiBox>
      )}

      {/* Sección de Documentos de Bienes (solo si posee bienes) */}
      {procesoData?.posee_bienes && (
        <SuiBox>
          <Typography variant="h6" gutterBottom>
            Documentos de Bienes
          </Typography>
          <DocumentosBienes
            procesoId={procesoId}
            identificador={procesoIdentificador}
            setError={setError}
          />
          <Divider sx={{ my: 3 }} />
        </SuiBox>
      )}

      {/* Sección de Documentos Laborales (solo si es empleado) */}
      {mostrarDocumentosLaborales && (
        <SuiBox>
          <Typography variant="h6" gutterBottom>
            Documentos Laborales
          </Typography>
          <DocumentosLaborales
            procesoId={procesoId}
            identificador={procesoIdentificador}
            setError={setError}
            clienteData={clienteData} // Pasar los datos del cliente
          />
          <Divider sx={{ my: 3 }} />
        </SuiBox>
      )}

      {/* Sección de Documentos Anexos */}
      <SuiBox>
        <Typography variant="h6" gutterBottom>
          Otros Docs. Anexos
        </Typography>
        <DocumentosAnexos
          procesoId={procesoId}
          identificador={procesoIdentificador}
          setError={setError}
        />
      </SuiBox>
    </SuiBox>
  );
};

DocumentosForm.propTypes = {
  procesoId: PropTypes.number.isRequired,
  setError: PropTypes.func.isRequired,
};

export default DocumentosForm;
